import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './MapPopup.css';

const SKIP_KEYS  = ['geometry', 'geom', 'the_geom', 'geom_3857', 'wkb_geometry'];
const IMAGE_KEYS = ['image_url', 'imagem_url', 'foto_url', 'foto', 'imagem', 'image'];
const URL_KEYS   = ['url', 'link', 'website', 'href'];

function formatLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getRegistroOrder(key) {
  const match = /^registro_(\d+)$/i.exec(String(key).trim());
  return match ? Number(match[1]) : null;
}

function isUrl(val) {
  if (typeof val !== 'string') return false;
  return /^https?:\/\//i.test(val.trim());
}

function isImageUrl(val) {
  if (!isUrl(val)) return false;
  return /\.(avif|bmp|gif|jpe?g|png|svg|webp)(\?|#|$)/i.test(String(val).trim());
}

function formatValue(key, val) {
  if (val === null || val === undefined || val === '') return '—';
  const s = String(val);
  if (/^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(s)) {
    try {
      const d = new Date(s);
      if (!isNaN(d)) return d.toLocaleDateString('pt-BR');
    } catch { /* keep original */ }
  }
  if (s === 'true'  || s === '1') return 'Sim';
  if (s === 'false' || s === '0') return 'Não';
  return s;
}

function GeomBadge({ type }) {
  if (!type) return null;
  const t = String(type).toUpperCase();
  let label = 'Feição'; let cls = 'badge-default';
  if (t.includes('POINT'))   { label = 'Ponto';   cls = 'badge-point';   }
  if (t.includes('LINE'))    { label = 'Linha';    cls = 'badge-line';    }
  if (t.includes('POLYGON')) { label = 'Polígono'; cls = 'badge-polygon'; }
  return <span className={`map-popup-badge ${cls}`}>{label}</span>;
}

function FeaturePanel({ feature, layerName }) {
  const [lightboxImage, setLightboxImage] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const props    = feature.properties || {};
  const geomType = feature.geometry?.type;

  const imageUrl = IMAGE_KEYS.map((k) => props[k]).find((v) => v && isUrl(v)) || null;

  const entries = Object.entries(props)
    .filter(([k]) => !SKIP_KEYS.includes(k) && !IMAGE_KEYS.includes(k))
    .sort(([keyA], [keyB]) => {
      const registroA = getRegistroOrder(keyA);
      const registroB = getRegistroOrder(keyB);

      if (registroA !== null && registroB !== null) {
        return registroA - registroB;
      }
      if (registroA !== null) return -1;
      if (registroB !== null) return 1;
      return keyA.localeCompare(keyB, 'pt-BR');
    });

  const PREVIEW_LIMIT = 6;
  const visibleEntries = expanded ? entries : entries.slice(0, PREVIEW_LIMIT);
  const hasMore = entries.length > PREVIEW_LIMIT;

  const title = layerName
    ? formatLabel(layerName.replace(/^saneamento:/i, ''))
    : 'Informações';

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!lightboxImage) return;
    const a = document.createElement('a');
    a.href = lightboxImage;
    a.download = lightboxImage.split('/').pop() || 'imagem';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  return (
    <>
      {/* Sub-header: title + geom badge */}
      <div className="map-popup-subheader">
        <span className="map-popup-title">{title}</span>
        <GeomBadge type={geomType} />
      </div>

      {imageUrl && (
        <div className="map-popup-image-wrap" onClick={() => setLightboxImage(imageUrl)}>
          <img src={imageUrl} alt="Foto" className="map-popup-image" />
          <span className="map-popup-image-hint">🔍 Ampliar</span>
        </div>
      )}

      {entries.length > 0 ? (
        <div className="map-popup-body">
          <table className="map-popup-table">
            <tbody>
              {visibleEntries.map(([key, val]) => {
                const isLink  = isUrl(String(val)) || URL_KEYS.includes(key.toLowerCase());
                const isImageLink = isImageUrl(String(val));
                const display = formatValue(key, val);
                return (
                  <tr key={key}>
                    <th>{formatLabel(key)}</th>
                    <td>
                      {isImageLink ? (
                        <button
                          type="button"
                          className="map-popup-inline-image-button"
                          onClick={() => setLightboxImage(String(val))}
                          title="Clique para ampliar a imagem"
                        >
                          <img
                            src={String(val)}
                            alt={formatLabel(key)}
                            className="map-popup-inline-image"
                          />
                          <span className="map-popup-inline-image-label">Ampliar imagem</span>
                        </button>
                      ) : isLink && isUrl(String(val)) ? (
                        <a href={String(val)} target="_blank" rel="noopener noreferrer" className="map-popup-link">
                          Abrir link ↗
                        </a>
                      ) : display}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hasMore && (
            <button className="map-popup-expand" onClick={() => setExpanded((v) => !v)}>
              {expanded ? '▲ Mostrar menos' : `▼ Ver mais ${entries.length - PREVIEW_LIMIT} campos`}
            </button>
          )}
        </div>
      ) : (
        <p className="map-popup-empty">Nenhum atributo disponível.</p>
      )}

      {lightboxImage && ReactDOM.createPortal(
        <div className="map-lightbox" onClick={() => setLightboxImage(null)}>
          <div className="map-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button className="map-lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
            <img src={lightboxImage} alt="Foto ampliada" className="map-lightbox-img" />
            <button className="map-lightbox-download" onClick={handleDownload}>⬇ Baixar</button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const MapPopup = ({ results, pixel, containerSize, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);

  // Reset tab when results change
  useEffect(() => { setActiveTab(0); }, [results]);

  // Close with Escape
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!results?.length || !pixel) return null;

  // Smart positioning — use max possible width (500px) so popup never clips
  const POPUP_W = Math.min(500, (containerSize?.width  || 800) - 24);
  const POPUP_H = 420;
  const cw = containerSize?.width  || 800;
  const ch = containerSize?.height || 600;

  let left = pixel[0] + 14;
  let top  = pixel[1] - 14;
  if (left + POPUP_W > cw - 10) left = pixel[0] - POPUP_W - 14;
  if (left < 10)                 left = 10;
  if (top  + POPUP_H > ch - 10) top  = ch - POPUP_H - 10;
  if (top  < 10)                 top  = 10;

  const safeTab = Math.min(activeTab, results.length - 1);
  const { feature, layerName } = results[safeTab];

  return (
    <div
      className="map-popup"
      style={{ left, top }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Top header: always visible */}
      <div className="map-popup-header">
        <span className="map-popup-header-label">
          {results.length > 1 ? `${results.length} camadas` : 'Informações'}
        </span>
        <button className="map-popup-close" onClick={onClose} title="Fechar">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/>
          </svg>
        </button>
      </div>

      {/* Tab bar — only when multiple results */}
      {results.length > 1 && (
        <div className="map-popup-tabs">
          {results.map((r, i) => {
            const short = formatLabel(
              (r.layerName || '').replace(/^saneamento:/i, '')
            );
            return (
              <button
                key={i}
                className={`map-popup-tab${i === safeTab ? ' map-popup-tab--active' : ''}`}
                onClick={() => setActiveTab(i)}
                title={short}
              >
                {short}
              </button>
            );
          })}
        </div>
      )}

      {/* Feature content for active tab */}
      <FeaturePanel
        key={safeTab}
        feature={feature}
        layerName={results.length === 1 ? layerName : null}
      />
    </div>
  );
};

export default MapPopup;
